import { GetStaticProps, GetStaticPaths } from "next";
import { serialize } from "next-mdx-remote/serialize";
import { MDXRemote, MDXRemoteSerializeResult } from "next-mdx-remote";
import fs from "fs";
import path from "path";
import MainLayout from "@/components/layouts/MainLayout";
import { NextSeo } from "next-seo";
import { parseFrontMatter } from "@/utils/parseFrontMatter";
import Link from "next/link";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";

interface BlogPostProps {
  mdxSource: MDXRemoteSerializeResult;
  frontMatter: {
    title?: string;
    description?: string;
    date?: string;
    image?: string;
    slug?: string;
  };
}

export default function BlogPost({ mdxSource, frontMatter }: BlogPostProps) {
  const publishedDate = new Date(frontMatter.date!).toISOString();

  return (
    <>
      <NextSeo
        title={`${frontMatter.title} | Web Performance & Engineering`}
        description={frontMatter.description}
        canonical={`https://amrik.live/blog/${frontMatter.slug}`}
        openGraph={{
          type: "article",
          article: {
            publishedTime: publishedDate,
          },
          url: `https://amrik.live/blog/${frontMatter.slug}`,
          title: frontMatter.title,
          description: frontMatter.description,
          images: [
            {
              url: frontMatter.image!,
              width: 1200,
              height: 630,
              alt: frontMatter.title,
            },
          ],
          siteName: "Amrik Malhans - Web Performance & Engineering",
        }}
      />
      <MainLayout>
        <Breadcrumb className="mt-8 mb-6">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link href="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link href="/blog">Blog</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="max-w-[200px] truncate md:max-w-none">
                {frontMatter.title}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <article className="w-full py-4">
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-semibold text-zinc-900 dark:text-zinc-100 mb-3">
              {frontMatter.title}
            </h1>
            
            {frontMatter.description && (
              <p className="text-base text-zinc-500 dark:text-zinc-400 mb-4">
                {frontMatter.description}
              </p>
            )}

            <div className="flex items-center gap-2 text-sm text-zinc-500 dark:text-zinc-400">
              <time>
                {new Date(frontMatter.date!).toLocaleDateString("en-US", {
                  month: "long",
                  day: "numeric",
                  year: "numeric",
                })}
              </time>
            </div>
          </div>

          <main className="text-block">
            <MDXRemote {...mdxSource} />
          </main>
        </article>
      </MainLayout>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async () => {
  const postsDirectory = path.join(process.cwd(), "content");
  const filenames = fs.readdirSync(postsDirectory);

  const paths = filenames
    .filter((filename) => filename.endsWith(".mdx"))
    .map((filename) => ({
      params: {
        slug: filename.replace(".mdx", ""),
      },
    }));

  return {
    paths,
    fallback: false,
  };
};

export const getStaticProps: GetStaticProps = async ({ params }) => {
  try {
    const postFilePath = path.join(
      process.cwd(),
      "content",
      `${params?.slug}.mdx`
    );

    const source = fs.readFileSync(postFilePath, "utf8");
    const { content, data } = parseFrontMatter(source);

    const mdxSource = await serialize(content, {
      mdxOptions: {
        remarkPlugins: [],
        rehypePlugins: [],
        development: process.env.NODE_ENV === "development",
      },
      scope: data,
    });

    return {
      props: {
        mdxSource,
        frontMatter: {
          ...data,
          slug: params?.slug,
        },
      },
    };
  } catch (error) {
    console.error("Error processing MDX:", error);
    return {
      notFound: true,
    };
  }
};
